import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";

export default function Auth() {
  const { user, authReady, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!authReady) return null;
  if (user) return <Navigate to="/home" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    if (mode === "forgot") {
      try {
        const { data } = await api.post("/auth/forgot-password", { email });
        setNotice(data?.message || "If an account exists for that email, a reset link has been sent.");
      } catch (err) {
        setError(err?.response?.data?.detail
          ? formatApiError(err.response.data.detail)
          : (err?.message || "Network error — could not reach the server."));
      } finally {
        setBusy(false);
      }
      return;
    }
    const res = mode === "login"
      ? await login(email, password)
      : await register(email, password, name || null);
    setBusy(false);
    if (res.ok) navigate("/home");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="auth-page">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 md:p-16 bg-surface">
        <div className="flex items-center gap-2">
          <img src="/brand/symbol-color.svg" alt="" className="h-7 w-auto" />
          <span className="font-display font-medium text-2xl tracking-tight">wandr</span>
        </div>

        <div className="max-w-md w-full mx-auto py-12">
          <p className="label-tag mb-3">
            {mode === "login" ? "Welcome back" : mode === "forgot" ? "Account recovery" : "Get started"}
          </p>
          <h1 className="heading-h1 text-4xl sm:text-5xl mb-2">
            {mode === "login" ? "Sign in." : mode === "forgot" ? "Reset password." : "Create account."}
          </h1>
          <div className="divider-strong my-6" />
          <p className="text-sand-600 text-base leading-relaxed mb-8">
            {mode === "forgot"
              ? "Enter your account email and we'll send you a link to reset your password."
              : "Forward any flight or hotel confirmation. Get a clean trip timeline. Export to your calendar in one click."}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="label-tag block mb-2">Name (optional)</label>
                <input
                  className="swiss-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label className="label-tag block mb-2">Email</label>
              <input
                type="email"
                required
                className="swiss-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
              />
            </div>
            {mode !== "forgot" && (
              <div>
                <label className="label-tag block mb-2">Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  className="swiss-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="auth-password-input"
                />
                {mode === "login" && (
                  <button
                    type="button"
                    onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                    className="mt-2 text-sm text-sand-600 hover:text-ember transition-colors"
                    data-testid="auth-forgot-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
            )}

            {error && (
              <div className="border border-error bg-red-50 text-error px-4 py-3 text-sm" data-testid="auth-error">
                {error}
              </div>
            )}
            {notice && (
              <div className="border border-ember bg-blue-50 text-ember px-4 py-3 text-sm" data-testid="auth-notice">
                {notice}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="btn-swiss btn-primary w-full disabled:opacity-50"
              data-testid="auth-submit"
            >
              {busy ? "..." : mode === "login" ? "Sign in" : mode === "forgot" ? "Send reset link" : "Create account"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setNotice(""); }}
            className="mt-6 text-sm text-sand-600 hover:text-ember transition-colors"
            data-testid="auth-toggle-mode"
          >
            {mode === "login"
              ? "No account? Create one →"
              : mode === "forgot"
                ? "← Back to sign in"
                : "Already have an account? Sign in →"}
          </button>
        </div>

        <div className="label-tag">© 2026 Wandr · MVP</div>
      </div>

      {/* Right: hero image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1596697340403-ac6e40523f54"
          alt="Airport terminal"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-surface/30" />
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <p className="label-tag mb-2 text-sand-900">Today / 02.2026</p>
          <p className="font-display font-medium text-3xl tracking-tight text-sand-950 max-w-md">
            Email in. Trips out. Calendar ready.
          </p>
        </div>
      </div>
    </div>
  );
}
